import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  ReplaySubject,
  combineLatest,
  distinctUntilChanged,
  map,
  shareReplay,
  skipWhile,
  switchMap,
  tap,
  filter,
} from 'rxjs';
import { SUPPORT_PID } from '../../../core/src/lib/conversation.constants';
import { PARTNER_ID_TOKEN } from '../../../core/src/lib/tokens';
import { ViewModeService } from '../../../core/src/lib/view-mode.service';
import { ConversationUIModule } from '../../../ui/src/conversation-ui.module';
import ConversationDetailsComponent from '../../../ui/src/pages/inbox-chat/components/inbox-info-pane/conversation-details/conversation-details.component';
import { InboxInfoDrawerService } from '../../../ui/src/pages/inbox-chat/components/inbox-info-pane/inbox-info-drawer.service';
import { ActiveSideView } from './types';
import { ConversationStatelessService } from '../../../core/src/lib/conversation-stateless.service';

@Component({
  imports: [
    MatSnackBarModule,
    CommonModule,
    ConversationUIModule,
    MatSidenavModule,
    MatIconModule,
    MatToolbarModule,
    TranslateModule,
    ConversationDetailsComponent,
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit {
  readonly isDrawerOpen$ = this.inboxInfoDrawerService.isOpenDrawer$;
  readonly viewMode$ = this.viewModeService.viewMode$;
  activeSideView: ActiveSideView = ActiveSideView.Details;

  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  readonly isTicketsEnabled = computed(() => this.partnerId() === SUPPORT_PID);

  private drawerMode$$ = new ReplaySubject<MatDrawerMode>(1);
  readonly drawerMode$ = combineLatest([this.drawerMode$$.pipe(distinctUntilChanged()), this.viewMode$]).pipe(
    tap(([drawerMode, viewMode]) => {
      this.inboxInfoDrawerService.setIsOpen(viewMode === 'modal' && drawerMode === 'side');
    }),
    map(([drawerMode, viewMode]) => (viewMode === 'sidebar' ? 'over' : drawerMode)),
    distinctUntilChanged(),
  );

  protected readonly details$ = this.route.paramMap.pipe(
    map((params) => params.get('id')),
    filter((id) => !!id),
    switchMap((id) => this.conversationStatelessService.getConversationDetail(id)),
    skipWhile((details) => !details),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private readonly conversationStatelessService: ConversationStatelessService,
    private readonly route: ActivatedRoute,
    private readonly inboxInfoDrawerService: InboxInfoDrawerService,
    private readonly viewModeService: ViewModeService,
  ) {}

  ngOnInit(): void {
    this.drawerMode$$.next(this.drawerMode);
  }

  back() {
    if (this.activeSideView === ActiveSideView.Details) {
      this.inboxInfoDrawerService.toggle();
    } else {
      this.activeSideView = ActiveSideView.Details;
    }
  }

  openTickets() {
    this.activeSideView = ActiveSideView.Tickets;
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.drawerMode$$.next(this.drawerMode);
  }

  private get drawerMode(): MatDrawerMode {
    return window.matchMedia('(max-width: 1100px)').matches ? 'over' : 'side';
  }
}
